import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../utils/api';
import { useDebounce } from '../hooks/useDebounce';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { 
  Cpu, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  Server, 
  Network, 
  Database, 
  Shield, 
  Zap, 
  Battery, 
  Plug 
} from 'lucide-react';

interface EquipmentItem {
  id: string;
  rack_id: number;
  name: string;
  equipment_type: string;
  u_height: number;
  u_position: number;
  mounting_position: string;
  is_zero_u: boolean;
  weight_kg: number;
  power_w?: number;
  ports: any[];
}

const TYPE_NAMES_RU: Record<string, string> = {
  server: 'Сервер',
  switch: 'Коммутатор',
  storage: 'СХД',
  firewall: 'Межсетевой экран',
  router: 'Маршрутизатор',
  pdu: 'PDU (Питание)',
  ups: 'ИБП',
  patch_panel: 'Патч-панель',
  unknown: 'Неизвестно',
};

const typeStyles: Record<string, string> = {
  server: 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-500/10 dark:border-blue-500/30',
  switch: 'text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-500/10 dark:border-green-500/30',
  storage: 'text-purple-700 bg-purple-50 border-purple-200 dark:text-purple-400 dark:bg-purple-500/10 dark:border-purple-500/30',
  firewall: 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-500/10 dark:border-red-500/30',
  router: 'text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-500/10 dark:border-orange-500/30',
  pdu: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-500/10 dark:border-amber-500/30',
  ups: 'text-yellow-700 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-500/10 dark:border-yellow-500/30',
  patch_panel: 'text-indigo-700 bg-indigo-50 border-indigo-200 dark:text-indigo-400 dark:bg-indigo-500/10 dark:border-indigo-500/30',
  unknown: 'text-slate-700 bg-slate-50 border-slate-200 dark:text-slate-400 dark:bg-slate-500/10 dark:border-slate-500/30',
};

const renderTypeIcon = (type: string) => {
  const t = type.toLowerCase();
  const iconClass = "w-4 h-4 text-text-secondary group-hover:text-accent transition-colors duration-200";
  switch (t) {
    case 'server': return <Server className={iconClass} />;
    case 'switch': return <Network className={iconClass} />;
    case 'storage': return <Database className={iconClass} />;
    case 'firewall': return <Shield className={iconClass} />;
    case 'router': return <Network className={iconClass} />;
    case 'pdu': return <Zap className={iconClass} />;
    case 'ups': return <Battery className={iconClass} />;
    case 'patch_panel': return <Plug className={iconClass} />;
    default: return <Cpu className={iconClass} />;
  }
};

interface MiniStatProps {
  label: string;
  value: number;
  sub: string;
  colorClass: string;
}

function MiniStat({ label, value, sub, colorClass }: MiniStatProps) {
  return (
    <div className="relative group bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-4 rounded-sm flex items-center justify-between shadow-sm overflow-hidden transition-[border-color] duration-300 hover:border-accent/50">
      {/* Subtle glow effect */}
      <div className="absolute inset-0 bg-gradient-to-tr from-accent/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      
      <div className="z-10 space-y-1">
        <div className="text-slate-400 text-[10px] font-semibold uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold font-mono text-text-primary tracking-tight">{value}</div>
        <div className="text-[10px] text-slate-500 font-medium">{sub}</div>
      </div>
      <div className="z-10 flex items-center">
        <span className={`w-3.5 h-3.5 rounded-full ${colorClass} shadow-sm animate-pulse`} style={{ animationDuration: '3s' }} />
      </div>
    </div>
  );
}

export default function EquipmentListPage() {
  const { dcCode } = useParams<{ dcCode: string }>();
  const { activeWorkspaceId } = useWorkspace();
  const [equipment, setEquipment] = useState<EquipmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 100;

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('name');
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const debouncedSearch = useDebounce(searchQuery, 400);

  useEffect(() => {
    if (!dcCode) {
      setEquipment([]);
      return;
    }
    
    setLoading(true);
    // Fetch equipment for this DC with pagination and search
    api.get(`/api/datacenters/${dcCode}/equipment`, {
      params: { skip, limit, search: debouncedSearch || undefined, workspace: activeWorkspaceId }
    })
      .then(res => {
        setEquipment(res.data);
      })
      .catch(() => {
        setEquipment([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dcCode, skip, debouncedSearch, activeWorkspaceId]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const filteredEquipment = useMemo(() => {
    return equipment.filter(item => {
      if (typeFilter === 'all') return true;
      return item.equipment_type.toLowerCase() === typeFilter.toLowerCase();
    });
  }, [equipment, typeFilter]);

  const sortedEquipment = useMemo(() => {
    const sorted = [...filteredEquipment];
    sorted.sort((a, b) => {
      let valA: any = '';
      let valB: any = '';

      switch (sortField) {
        case 'name':
          valA = a.name || '';
          valB = b.name || '';
          break;
        case 'type':
          valA = TYPE_NAMES_RU[a.equipment_type] || a.equipment_type;
          valB = TYPE_NAMES_RU[b.equipment_type] || b.equipment_type;
          break;
        case 'location':
          valA = (a.rack_id * 1000) + (a.u_position || 0);
          valB = (b.rack_id * 1000) + (b.u_position || 0);
          break;
        case 'height':
          valA = a.u_height || 0;
          valB = b.u_height || 0;
          break;
        case 'weight':
          valA = a.weight_kg || 0;
          valB = b.weight_kg || 0;
          break;
        case 'power':
          valA = a.power_w || 0;
          valB = b.power_w || 0;
          break;
        case 'ports':
          valA = a.ports?.length || 0;
          valB = b.ports?.length || 0;
          break;
        default:
          return 0;
      }

      if (valA === valB) return 0;
      if (valA < valB) return sortAsc ? -1 : 1;
      return sortAsc ? 1 : -1;
    });
    return sorted;
  }, [filteredEquipment, sortField, sortAsc]);

  const renderSortableHeader = (field: string, label: string, alignClass: string = 'text-left') => {
    const isActive = sortField === field;
    return (
      <th
        onClick={() => handleSort(field)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSort(field);
          }
        }}
        tabIndex={0}
        role="button"
        aria-sort={isActive ? (sortAsc ? 'ascending' : 'descending') : 'none'}
        className={`px-4 py-3 text-[10px] font-bold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary select-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none transition-colors duration-200 ${alignClass}`}
      >
        <div className={`flex items-center gap-1.5 ${alignClass === 'text-right' ? 'justify-end' : alignClass === 'text-center' ? 'justify-center' : ''}`}>
          <span>{label}</span>
          <span className={`text-[9px] transition-opacity duration-200 ${isActive ? 'text-accent opacity-100' : 'opacity-30 hover:opacity-75'}`}>
            {isActive ? (sortAsc ? '↑' : '↓') : '↕'}
          </span>
        </div>
      </th>
    );
  };

  if (!dcCode) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-6 rounded-sm shadow-sm">
          <h2 className="text-lg font-semibold text-text-primary mb-2">Выберите дата-центр</h2>
          <p className="text-gray-400 text-sm">Пожалуйста, выберите ЦОД в верхнем меню, чтобы просмотреть реестр оборудования.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in font-sans">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-800/20 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Cpu size={24} className="text-accent" />
            Реестр оборудования (ЦОД: {dcCode})
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Инвентарный перечень всех серверов, свитчей, маршрутизаторов, патч-панелей и PDU.
          </p>
        </div>
      </div>

      {/* KPI Cards Summary Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat
          label="Всего устройств"
          value={equipment.length}
          sub="Зарегистрировано в ЦОД"
          colorClass="bg-accent/40 border border-accent/60"
        />
        <MiniStat
          label="Серверное оборудование"
          value={equipment.filter(e => e.equipment_type === 'server').length}
          sub="Вычислительные узлы"
          colorClass="bg-blue-500/40 border border-blue-500/60"
        />
        <MiniStat
          label="Сетевое оборудование"
          value={equipment.filter(e => ['switch', 'router', 'firewall'].includes(e.equipment_type)).length}
          sub="Коммутаторы и маршрутизаторы"
          colorClass="bg-green-500/40 border border-green-500/60"
        />
        <MiniStat
          label="Пассивное & Питание"
          value={equipment.filter(e => ['pdu', 'ups', 'patch_panel'].includes(e.equipment_type)).length}
          sub="PDU, ИБП и патч-панели"
          colorClass="bg-purple-500/40 border border-purple-500/60"
        />
      </div>

      {/* Filters and Pagination Bar */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-slate-900/40 backdrop-blur-md border border-slate-800/50 p-4 rounded-sm shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {/* Search input */}
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Поиск оборудования..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setSkip(0);
              }}
              className="w-full pl-9 pr-4 py-2 bg-slate-900/40 border border-slate-800/50 rounded-sm text-sm text-text-primary focus:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none transition-colors duration-200"
            />
          </div>

          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-2 bg-slate-900/40 border border-slate-800/50 rounded-sm text-sm text-text-primary focus:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none cursor-pointer transition-colors duration-200 w-full sm:w-auto"
          >
            <option value="all" className="bg-slate-900 text-text-primary">Все типы устройств</option>
            {Object.keys(TYPE_NAMES_RU).map(type => (
              <option key={type} value={type} className="bg-slate-900 text-text-primary">
                {TYPE_NAMES_RU[type]}
              </option>
            ))}
          </select>
        </div>

        {/* Paginator */}
        <div className="flex gap-2 w-full md:w-auto justify-end">
          <button
            onClick={() => setSkip(s => Math.max(0, s - limit))}
            disabled={skip === 0 || loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-900/40 border border-slate-800/50 rounded-sm text-sm text-text-secondary hover:bg-slate-800/25 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
          >
            <ChevronLeft size={16} />
            <span>Назад</span>
          </button>
          <button
            onClick={() => setSkip(s => s + limit)}
            disabled={equipment.length < limit || loading}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-900/40 border border-slate-800/50 rounded-sm text-sm text-text-secondary hover:bg-slate-800/25 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
          >
            <span>Вперед</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Equipment Table Container */}
      <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800/50 rounded-sm shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-20">
            <Cpu size={24} className="animate-spin text-accent mx-auto mb-2" />
            <p className="text-gray-400 text-sm">Загрузка реестра оборудования...</p>
          </div>
        ) : sortedEquipment.length === 0 ? (
          <div className="text-center py-20">
            <Cpu size={32} className="text-slate-600 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">Устройства не найдены</p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full scrollbar-thin">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-800/80 bg-slate-900/90 backdrop-blur-md">
                  {renderSortableHeader('name', 'Имя устройства')}
                  {renderSortableHeader('type', 'Тип устройства')}
                  {renderSortableHeader('location', 'Расположение в стойке')}
                  {renderSortableHeader('height', 'Высота', 'text-center')}
                  {renderSortableHeader('weight', 'Вес', 'text-right')}
                  {renderSortableHeader('power', 'Мощность', 'text-right')}
                  {renderSortableHeader('ports', 'Порты', 'text-center')}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/30">
                {sortedEquipment.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-slate-800/25 transition-colors duration-200 text-sm border-slate-800/20"
                  >
                    {/* Clickable Device details Link with vector icon block */}
                    <td className="px-4 py-3">
                      <Link
                        to={`/${dcCode}/equipment/${item.id}`}
                        className="font-semibold text-text-primary hover:text-accent hover:underline flex items-center gap-2.5 transition-colors duration-200 group focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
                      >
                        <span className="flex items-center justify-center p-1 bg-slate-900/60 border border-slate-800/40 rounded-xs">
                          {renderTypeIcon(item.equipment_type)}
                        </span>
                        <span>{item.name || 'Без имени'}</span>
                      </Link>
                    </td>
                    {/* Styled Type Badge */}
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        typeStyles[item.equipment_type] || typeStyles.unknown
                      }`}>
                        {TYPE_NAMES_RU[item.equipment_type] || item.equipment_type}
                      </span>
                    </td>
                    {/* Styled Location tag and Rack link */}
                    <td className="px-4 py-3">
                      <Link
                        to={`/${dcCode}/racks/${item.rack_id}`}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800/50 rounded-xs text-[10px] font-mono text-text-secondary hover:text-text-primary transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent/50 outline-none"
                        title="Открыть стойку"
                      >
                        <span className="opacity-60">Rack {item.rack_id}</span>
                        <span className="opacity-40">·</span>
                        <span className="text-accent font-semibold">
                          {item.is_zero_u ? 'Zero-U' : `U${item.u_position}`}
                        </span>
                        <span className="opacity-50">({item.mounting_position})</span>
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-xs">
                      {item.u_height}U
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">
                      {typeof item.weight_kg === 'number' ? `${item.weight_kg} кг` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs">
                      {typeof item.power_w === 'number' ? `${item.power_w} Вт` : '—'}
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-xs text-accent">
                      {item.ports?.length || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
